/**
 * The organization's billing picture, derived (ADR-0078 slice 5).
 *
 * There is deliberately NO new endpoint and no cross-workspace invoice: ADR-0076 settled
 * that a Organizzazione is a back-office record with **one Stripe Customer but a cap per
 * DEPLOYMENT** — billing stays per workspace. So the org view's job is to say, honestly
 * and at a glance, what the company is paying for and whether anything needs attention.
 * The data is what `/api/onboarding/organizations` already returns.
 */
import type { IdentityOrgWorkspace } from "@/lib/api-onboarding";

export interface OrgBillingSummary {
  workspaces: number;
  /** Workspaces on a live plan (`active` / `trialing`). */
  billed: number;
  /** Payment failed and the grace period is running — the one that needs a human. */
  pastDue: number;
  /** No subscription at all: comp, on-prem-style, or simply not subscribed yet. */
  unplanned: number;
  /** Distinct plan names in play, sorted — a company on three plans should SEE that. */
  plans: string[];
}

/** `active`/`trialing` entitle; `past_due` still entitles during grace but is a problem. */
const LIVE = new Set(["active", "trialing"]);

export function orgBillingSummary(workspaces: IdentityOrgWorkspace[]): OrgBillingSummary {
  const plans = new Set<string>();
  let billed = 0;
  let pastDue = 0;
  let unplanned = 0;
  for (const w of workspaces) {
    const status = w.subscription_status ?? null;
    if (w.plan_name) plans.add(w.plan_name);
    if (status === "past_due") pastDue += 1;
    else if (status && LIVE.has(status)) billed += 1;
    else if (!status) unplanned += 1;
  }
  return {
    workspaces: workspaces.length,
    billed,
    pastDue,
    unplanned,
    plans: [...plans].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * The one-line summary under an organization's name. Returns `null` when there is
 * nothing to say (no workspaces) rather than an empty-looking "0 workspace" — an
 * organization founded a minute ago is not a billing anomaly.
 */
export function orgBillingLine(s: OrgBillingSummary): string | null {
  if (s.workspaces === 0) return null;
  const parts = [`${s.workspaces} workspace`];
  if (s.plans.length > 0) parts.push(s.plans.join(" · "));
  if (s.unplanned > 0) parts.push(`${s.unplanned} senza piano`);
  if (s.pastDue > 0) parts.push(`${s.pastDue} in ritardo`);
  return parts.join(" — ");
}
